import { CLAIMS_MADE_TYPES, COVER_TYPE_DEFAULT } from './factFinds'

export const IMPORTANT_INFO_LINKS = {
  brokerCode: 'https://niba.com.au/code-of-practice',
  terms: 'https://benchmarkinsurance.com.au/wp-content/uploads/2026/02/Benchmark-Terms-of-Engagement-Mar-26.pdf',
  fsg: 'https://benchmarkinsurance.com.au/wp-content/uploads/2026/02/Benchmark-FSG-Mar-26.pdf',
  privacy: 'https://benchmarkinsurance.com.au/docs/privacy-policy',
  importantNotice: 'https://benchmarkinsurance.com.au/',
}

export const IMPORTANT_INFO_ITEMS = [
  { key: 'brokerCode', label: 'Broker Code', url: IMPORTANT_INFO_LINKS.brokerCode },
  { key: 'terms', label: 'Terms of Engagement', url: IMPORTANT_INFO_LINKS.terms },
  { key: 'fsg', label: 'Financial Services Guide', url: IMPORTANT_INFO_LINKS.fsg },
  { key: 'privacy', label: 'Privacy Policy', url: IMPORTANT_INFO_LINKS.privacy },
  { key: 'importantNotice', label: 'Important Notice', url: IMPORTANT_INFO_LINKS.importantNotice },
]

export const ADVICE_MODE_OPTIONS = ['Personal Advice', 'General Advice']
export const CLASSIFICATION_OPTIONS = ['Retail', 'Wholesale']
export const COMMERCIAL_MOTOR_UNDER_2T_OPTIONS = ['Yes', 'No', 'Unsure']
export const CONSENT_METHOD_OPTIONS = ['Written confirmation', 'Verbal then confirmed in writing', 'Email', 'Phone']

const asArray = (value) => Array.isArray(value) ? value : []

export const makeAuditEntry = (type, action, broker, changes = []) => ({
  type,
  action,
  broker,
  timestamp: new Date().toISOString(),
  changes,
})

export const makeActivityEntry = (type, text, icon = '📝') => ({
  type,
  text,
  timestamp: new Date().toISOString(),
  icon,
})

export const getClientPolicyType = (client) => {
  if (client?.policy_type) return client.policy_type
  const firstFactFind = asArray(client?.fact_finds)[0]
  return firstFactFind?.type || ''
}

export const getLatestComplianceEntry = (client) => {
  return asArray(client?.audit_log).find((entry) => entry?.snapshot) || null
}

export const getStoredComplianceSnapshot = (client) => {
  return getLatestComplianceEntry(client)?.snapshot || {}
}

export const getBaseClassification = (policyType, snapshot = {}) => {
  if (policyType === 'Commercial Motor') {
    if (snapshot.commercial_motor_under_2t === 'No') return 'Wholesale'
    return 'Retail'
  }

  return COVER_TYPE_DEFAULT[policyType] || 'Wholesale'
}

export const buildComplianceState = (client, overrides = {}) => {
  const stored = getStoredComplianceSnapshot(client)
  const merged = { ...stored, ...overrides }
  const policyType = overrides.policy_type || merged.policy_type || getClientPolicyType(client)
  const brokerFeeCharged = merged.broker_fee_charged !== false
  const commissionCollected = merged.commission_collected !== false
  const adviceMode = merged.advice_mode || (brokerFeeCharged ? 'Personal Advice' : 'General Advice')
  const baseClassification = getBaseClassification(policyType, merged)
  const effectiveClassification = merged.classification_override || baseClassification
  const claimsMade = CLAIMS_MADE_TYPES.includes(policyType)
  const informedConsentRequired = effectiveClassification === 'Retail' && adviceMode === 'Personal Advice' && commissionCollected

  const blockers = []
  if (!merged.important_info_sent_at) blockers.push('Important Information has not been logged')
  if (!merged.tmd_checked_at) blockers.push('TMD has not been checked')
  if (claimsMade && !merged.claims_made_notice_at) blockers.push('Claims-made notice has not been recorded')
  if (informedConsentRequired && !merged.informed_consent_received_at) blockers.push('Informed consent is required before placement')

  const completedChecks = [
    !!merged.important_info_sent_at,
    !!merged.tmd_checked_at,
    !claimsMade || !!merged.claims_made_notice_at,
    !informedConsentRequired || !!merged.informed_consent_received_at,
  ]

  return {
    ...merged,
    policy_type: policyType,
    broker_fee_charged: brokerFeeCharged,
    commission_collected: commissionCollected,
    advice_mode: adviceMode,
    base_classification: baseClassification,
    effective_classification: effectiveClassification,
    duty_label: effectiveClassification === 'Wholesale'
      ? 'Duty of Disclosure'
      : 'Duty to Take Reasonable Care Not to Make a Misrepresentation',
    is_claims_made: claimsMade,
    informed_consent_required: informedConsentRequired,
    blockers,
    completion_total: completedChecks.length,
    completion_done: completedChecks.filter(Boolean).length,
  }
}

export const buildRequiredInfoCompletionUpdates = (state, options = {}) => {
  const timestamp = options.timestamp || new Date().toISOString()
  const method = options.method || 'CRM required info email'
  const consentMethod = options.consentMethod || 'Sent by email'

  return {
    important_info_sent_at: state.important_info_sent_at || timestamp,
    important_info_method: state.important_info_method || method,
    tmd_checked_at: state.tmd_checked_at || timestamp,
    claims_made_notice_at: state.is_claims_made ? (state.claims_made_notice_at || timestamp) : state.claims_made_notice_at || null,
    informed_consent_received_at: state.informed_consent_required ? (state.informed_consent_received_at || timestamp) : state.informed_consent_received_at || null,
    informed_consent_method: state.informed_consent_required ? (state.informed_consent_method || consentMethod) : state.informed_consent_method || '',
  }
}

export const diffComplianceState = (previousState, nextState) => {
  const labels = {
    policy_type: 'Policy type',
    base_classification: 'Base classification',
    effective_classification: 'Effective classification',
    advice_mode: 'Advice mode',
    broker_fee_charged: 'Broker fee charged',
    commission_collected: 'Commission collected',
    commercial_motor_under_2t: 'Commercial Motor under 2T',
    classification_override: 'Classification override',
    classification_override_reason: 'Override reason',
    important_info_sent_at: 'Important Information sent',
    important_info_method: 'Important Information method',
    informed_consent_received_at: 'Informed consent recorded',
    informed_consent_method: 'Informed consent method',
    tmd_checked_at: 'TMD checked',
    claims_made_notice_at: 'Claims-made notice recorded',
  }

  const keys = Object.keys(labels)

  return keys
    .map((key) => {
      const before = previousState?.[key]
      const after = nextState?.[key]
      if ((before || '') === (after || '')) return null
      return `${labels[key]}: "${before || '—'}" -> "${after || '—'}"`
    })
    .filter(Boolean)
}

export const formatComplianceSummary = (state) => {
  if (!state.policy_type) return 'No policy type selected'
  return [
    `${state.policy_type}`,
    `${state.effective_classification}`,
    `${state.advice_mode}`,
    state.is_claims_made ? 'Claims-Made' : 'Occurrence',
  ].join(' • ')
}

export const buildImportantInfoEmail = ({ client, brokerName }) => {
  const name = client?.contact_name || client?.company_name || 'there'
  const subject = `Important Information - Benchmark Insurance Brokers`
  const linkList = IMPORTANT_INFO_ITEMS
    .map((item) => `- ${item.label}: ${item.url}`)
    .join('\n')

  const text = [
    `Hi ${name},`,
    '',
    'Thank you for the opportunity to assist with your insurance.',
    '',
    'We will now research the market to find you the best available options from Australia\'s largest and most reputable insurers.',
    '',
    'Before we proceed, please review the following Benchmark Important Information documents:',
    '',
    linkList,
    '',
    'Please review these carefully, including the information about our fees and other important information, and let us know if you have any questions.',
    '',
    `${brokerName || 'Your broker'}`,
    'Benchmark Insurance Brokers',
  ].join('\n')

  const html = `
    <p>Hi ${name},</p>
    <p>Thank you for the opportunity to assist with your insurance.</p>
    <p>We will now research the market to find you the best available options from Australia's largest and most reputable insurers.</p>
    <p>Before we proceed, please review the following Benchmark Important Information documents:</p>
    <ul>
      ${IMPORTANT_INFO_ITEMS.map((item) => `<li><a href="${item.url}">${item.label}</a></li>`).join('')}
    </ul>
    <p>Please review these carefully, including the information about our fees and other important information, and let us know if you have any questions.</p>
    <p>${brokerName || 'Your broker'}<br/>Benchmark Insurance Brokers</p>
  `

  return { subject, text, html }
}

export const buildNoticeBlocks = (state) => {
  const blocks = []

  if (state.advice_mode === 'Personal Advice') {
    blocks.push({
      title: 'Personal Advice Record',
      body: 'We have provided personal advice based on the information you gave us about your objectives, needs and circumstances. Please review the recommendation carefully and tell us straight away if anything is incorrect or has changed.',
    })
  } else {
    blocks.push({
      title: 'General Advice Warning',
      body: 'Any advice we provide is general advice only and does not take into account your objectives, financial situation or needs. You should consider whether the product is appropriate for you.',
    })
  }

  if (state.effective_classification === 'Wholesale') {
    blocks.push({
      title: 'Duty of Disclosure',
      body: 'All the information you provide will be provided to the insurers on your behalf.<br/><br/>I will also let you know about your duty of disclosure.<br/><br/>When answering questions, you must be honest and tell us anything a reasonable person would include.<br/><br/>Your insurance company may reduce or refuse a claim if you have not answered these questions in this way.',
    })
  } else {
    blocks.push({
      title: 'Duty to Take Reasonable Care Not to Make a Misrepresentation',
      body: 'To ensure you meet your duty, when you contact us to advise of any information that is incorrect or has changed, the updated information you give us must be truthful, accurate and complete.<br/><br/>If you fail to meet your duty, the insurer may be able to cancel your contract or reduce the amount it will pay if you make a claim, or both.<br/><br/>If your failure is fraudulent, the insurer may be able to refuse to pay a claim and treat the contract as if it never existed.',
    })
  }

  if (state.is_claims_made) {
    blocks.push({
      title: 'Claims-Made Notice',
      body: 'This is a claims-made policy. Cover generally responds to claims first made against you and notified to the insurer during the policy period. Known facts or circumstances must be disclosed before inception and continuous cover is important.',
    })
  }

  blocks.push({
    title: 'Our Remuneration',
    body: 'In return for the services we provide we may receive a commission, usually between 5% and 26.5% of the premium paid (excluding relevant taxes, charges and levies) which is paid to us by the insurer, and a fee payable by you.',
  })

  return blocks
}

export const renderNoticeBlocksHtml = (blocks) => {
  return blocks.map((block) => `
    <div style="margin:0 0 14px 0;line-height:1.7;">
      <strong>${block.title}</strong><br/>
      ${block.body}
    </div>
  `).join('')
}

export const renderNoticeBlocksText = (blocks) => {
  return blocks.map((block) => `${block.title}\n${block.body}`).join('\n\n')
}

export const persistClientCompliance = async ({
  supabase,
  client,
  nextState,
  userName,
  activityText = 'Compliance updated',
}) => {
  const previousState = buildComplianceState(client)
  const changes = diffComplianceState(previousState, nextState)
  const auditEntry = makeAuditEntry('compliance', 'Compliance updated', userName, changes)
  auditEntry.snapshot = nextState

  const activityEntry = makeActivityEntry('compliance', `${activityText} by ${userName}`, '⚖️')
  const updatedAudit = [auditEntry, ...asArray(client?.audit_log)]
  const updatedActivity = [activityEntry, ...asArray(client?.activity_log)]

  await supabase
    .from('clients')
    .update({
      audit_log: updatedAudit,
      activity_log: updatedActivity,
    })
    .eq('id', client.id)

  return {
    audit_log: updatedAudit,
    activity_log: updatedActivity,
  }
}

export const logClientEmailAction = async ({
  supabase,
  client,
  userName,
  emailType,
  subject,
  recipient,
  snapshotUpdates = {},
  activityText,
}) => {
  const nextState = buildComplianceState(client, snapshotUpdates)
  const auditEntry = makeAuditEntry('email', `${emailType} sent`, userName, [
    `To: ${recipient || '—'}`,
    `Subject: ${subject || '—'}`,
  ])
  auditEntry.snapshot = nextState

  const activityEntry = makeActivityEntry('email', activityText || `${emailType} sent by ${userName}`, '📧')
  const updatedAudit = [auditEntry, ...asArray(client?.audit_log)]
  const updatedActivity = [activityEntry, ...asArray(client?.activity_log)]

  await supabase
    .from('clients')
    .update({
      audit_log: updatedAudit,
      activity_log: updatedActivity,
    })
    .eq('id', client.id)

  try {
    await supabase.from('email_audit_log').insert({
      client_id: client.id,
      email_type: emailType,
      recipient,
      subject,
      sent_by: userName,
      payload: nextState,
      created_at: new Date().toISOString(),
    })
  } catch (error) {
    console.warn('email_audit_log insert skipped:', error?.message || error)
  }

  return {
    audit_log: updatedAudit,
    activity_log: updatedActivity,
    next_state: nextState,
  }
}
